import React, { useState, useEffect, useRef, useCallback } from 'react'

import * as SelectPrimitive from '@radix-ui/react-select'
import { CheckIcon, MapPin } from 'lucide-react'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { FeedbackType } from '@/core/api/entities/song/feedbackType'
import { Song } from '@/core/api/entities/song/song'
import { SongFeedback } from '@/core/api/entities/song/songFeedback'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useAddPlaylistToFavorites } from '@/core/hooks/usePlaylist'
import { useTranslation } from '@/core/i18n/useTranslation'
import { AssignPlaylistModal } from '@/features/music/components/AssignPlaylistModal'
import { ChooseNegativeFeedbackModal } from '@/features/music/components/ChooseNegativeFeedbackModal'
import { PlaylistHeader } from '@/features/music/components/PlaylistHeader'
import { PlaylistSongs } from '@/features/music/components/PlaylistSongs'
import { TrackPlayer } from '@/features/music/components/TrackPlayer'
import {
	resetChatbaseLauncherBottomOffset,
	setChatbaseLauncherBottomOffset,
} from '@/shared/analytics/ChatbaseWidget'
import { usePlaylistTracking } from '@/shared/analytics/usePlaylistTracking'
import { Button } from '@/shared/components/atoms/Button'
import Icon from '@/shared/components/atoms/Icon'
import { Input } from '@/shared/components/atoms/Input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { PageLayout } from '@/shared/components/organisms/Layout/PageLayout/PageLayout'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { cn } from '@/shared/utils/helpers'

interface PlaylistDetailsPageUIProps {
	playlist: Playlist | undefined
	isLoading: boolean
	searchQuery: string
	onSearchChange: (query: string) => void
	filteredSongs: Song[]
	onBack: () => void
	onRateClick: () => void
	ratePlaylistUrl: string | null
	songFeedbacks: Map<number, SongFeedback>
	isLoadingFeedback: boolean
	showRateModal: boolean
	onCloseRateModal: () => void
	statusFilter: 'all' | 'disliked'
	onStatusFilterChange: (filter: 'all' | 'disliked') => void
	onSubmitFeedback: (
		songId: number,
		feedbackTypeId: number,
		onSuccess?: () => void,
		isDislike?: boolean
	) => void
	feedbackTypes: FeedbackType[] | undefined
	playlistFeedbacks: SongFeedback[] | undefined
	showFeedbackDropModal: boolean
	onCloseFeedbackDropModal: () => void
	feedbackDropRatio: number | null
	setShowFeedbackDropModal: (show: boolean) => void
	showDislikeFeedbackModal: boolean
	onCloseDislikeFeedbackModal: () => void
	dislikeFeedbackUrl: string | null
	allFeedbackTypes: FeedbackType[]
	initialFavoriteState?: boolean
	onRequestModificationsClick?: () => void
	requestModificationsUrl: string | null
	showRequestModificationsModal: boolean
	onCloseRequestModificationsModal: () => void
	onApprovePlaylist?: () => void
	isApproving?: boolean
	showNegativeFeedbackOptionsModal: boolean
	setShowNegativeFeedbackOptionsModal: (show: boolean) => void
	onCloseNegativeFeedbackOptionsModal: () => void
	selectedSongForFeedback: number | null
	setSelectedSongForFeedback: (songId: number | null) => void
	currentLanguage: string
	isSubmittingFeedback: boolean
	canRequestModifications: boolean
	showAssignPlaylistModal: boolean
	onAssignPlaylistModalChange: (open: boolean) => void
	canAssignPlaylists?: boolean
	// Optional player props
	showFeedbackIcons?: boolean
	showCloseButton?: boolean
}

const PlaylistDetailsPageUI: React.FC<PlaylistDetailsPageUIProps> = ({
	playlist,
	isLoading,
	searchQuery,
	onSearchChange,
	filteredSongs,
	onBack,
	onRateClick,
	ratePlaylistUrl,
	songFeedbacks,
	isLoadingFeedback,
	showRateModal,
	onCloseRateModal,
	statusFilter,
	onStatusFilterChange,
	onSubmitFeedback,
	feedbackTypes,
	playlistFeedbacks,
	showFeedbackDropModal,
	onCloseFeedbackDropModal,
	feedbackDropRatio,
	setShowFeedbackDropModal,
	showDislikeFeedbackModal,
	onCloseDislikeFeedbackModal,
	dislikeFeedbackUrl,
	allFeedbackTypes,
	initialFavoriteState = false,
	onRequestModificationsClick,
	requestModificationsUrl,
	showRequestModificationsModal,
	onCloseRequestModificationsModal,
	onApprovePlaylist,
	isApproving = false,
	showNegativeFeedbackOptionsModal,
	setShowNegativeFeedbackOptionsModal,
	onCloseNegativeFeedbackOptionsModal,
	selectedSongForFeedback,
	setSelectedSongForFeedback,
	currentLanguage,
	canRequestModifications,
	showAssignPlaylistModal,
	onAssignPlaylistModalChange,
	canAssignPlaylists = false,
	showFeedbackIcons = true,
	showCloseButton = true,
	isSubmittingFeedback,
}) => {
	const { t, getTranslation } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const [isDesktop, setIsDesktop] = useState(() => {
		// Tailwind md breakpoint is 768px
		if (typeof window === 'undefined') return true
		return window.matchMedia('(min-width: 768px)').matches
	})
	const {
		trackSongSearch,
		trackSongAutoplay,
		trackNextSong,
		trackPreviousSong,
		trackSongPause,
		trackSongResume,
		trackFeedbackPositive,
		trackFeedbackNegative,
		trackPlayerClose,
	} = usePlaylistTracking()
	const [isFavorite, setIsFavorite] = useState(initialFavoriteState)
	const [currentPlayingSong, setCurrentPlayingSong] = useState<Song | null>(
		null
	)
	const [hoveredFeedbackSongId, setHoveredFeedbackSongId] = useState<
		number | null
	>(null)
	const [isPlayerPlaying, setIsPlayerPlaying] = useState(false)
	const [isFavoriting, setIsFavoriting] = useState(false)
	const lastMutationRef = React.useRef<{
		playlistId: number
		favorite: boolean
		timestamp: number
	} | null>(null)
	const lastMutationInitiatedRef = React.useRef<{
		playlistId: number
		timestamp: number
	} | null>(null)
	const [volume, setVolume] = useState(1)
	const [isMuted, setIsMuted] = useState(false)
	const audioElementRef = React.useRef<HTMLAudioElement | null>(null)
	const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Keep in sync with viewport changes (show title header on desktop only)
	useEffect(() => {
		if (typeof window === 'undefined') return

		const mediaQuery = window.matchMedia('(min-width: 768px)')
		const onChange = () => setIsDesktop(mediaQuery.matches)

		onChange()

		// Safari < 14 uses addListener/removeListener
		if (typeof mediaQuery.addEventListener === 'function') {
			mediaQuery.addEventListener('change', onChange)
			return () => mediaQuery.removeEventListener('change', onChange)
		}

		mediaQuery.addListener(onChange)
		return () => mediaQuery.removeListener(onChange)
	}, [])

	const pageTitle = React.useMemo(() => {
		if (!playlist) return t('navigation.playlist_detail', 'Playlist Detail')
		// `currentLanguage` comes from parent and mirrors app language state
		return getTranslation(
			playlist.titleTranslations || {},
			currentLanguage,
			playlist.title || ''
		)
	}, [playlist, currentLanguage, getTranslation, t])

	// Track song search with debounce (500ms after user stops typing)
	useEffect(() => {
		if (searchDebounceRef.current) {
			clearTimeout(searchDebounceRef.current)
		}

		if (searchQuery.trim() && playlist) {
			searchDebounceRef.current = setTimeout(() => {
				trackSongSearch(
					{
						playlistId: playlist.id,
						playlistTitle: playlist.title,
					},
					searchQuery.trim()
				)
			}, 500)
		}

		return () => {
			if (searchDebounceRef.current) {
				clearTimeout(searchDebounceRef.current)
			}
		}
	}, [searchQuery, playlist, trackSongSearch])

	// Mutation for adding playlist to favorites
	const addToFavoritesMutation = useAddPlaylistToFavorites({
		config: useAccountConfig({}),
		options: {
			onSuccess: (
				_: void,
				variables: { playlistId: number; favorite: boolean }
			) => {
				setIsFavoriting(false)
				setIsFavorite(variables.favorite)

				// Prevent duplicate toasts by checking if we've already shown one for this mutation
				const now = Date.now()
				const lastMutation = lastMutationRef.current
				if (
					lastMutation &&
					lastMutation.playlistId === variables.playlistId &&
					lastMutation.favorite === variables.favorite &&
					now - lastMutation.timestamp < 2000 // Within 2 seconds
				) {
					return // Skip duplicate toast
				}

				showNotification({
					message: variables.favorite
						? t(
								'entities.music.playlist_added_to_favorites',
								'Playlist added to favorites'
							)
						: t(
								'entities.music.playlist_removed_from_favorites',
								'Playlist removed from favorites'
							),
					type: 'success',
					duration: 3,
				})

				// Update ref AFTER showing toast to track when it was shown
				lastMutationRef.current = {
					playlistId: variables.playlistId,
					favorite: variables.favorite,
					timestamp: now,
				}
			},
			onError: () => {
				setIsFavoriting(false)
				showNotification({
					message: isFavorite
						? t(
								'entities.music.playlist_remove_from_favorites_error',
								'Error removing playlist from favorites'
							)
						: t(
								'entities.music.playlist_add_to_favorites_error',
								'Error adding playlist to favorites'
							),
					type: 'error',
					duration: 3,
				})
			},
		},
	})

	const handleFeedbackClick = (song: Song, isPositive: boolean) => {
		if (feedbackDropRatio && feedbackDropRatio >= 0.3) {
			setShowFeedbackDropModal(true)
			return
		}

		const positiveFeedback = feedbackTypes?.filter((feedbackType) =>
			feedbackType.isPositive()
		)
		const neutralFeedback = feedbackTypes?.filter((feedbackType) =>
			feedbackType.isNeutral()
		)
		const currentFeedback = playlistFeedbacks?.find(
			(feedback: SongFeedback) =>
				feedback.songId === song.id && feedback.playerId === null
		)

		const newFeedback = isPositive
			? feedbackTypes?.find(
					(feedbackType) => feedbackType.isPositive() === isPositive
				)
			: feedbackTypes?.find((feedbackType) => feedbackType.isNegativeDefault())

		const isSameFeedback =
			currentFeedback?.feedbackTypeId === newFeedback?.id ||
			feedbackTypes?.filter(
				(feedbackType) =>
					feedbackType.isNegative &&
					feedbackType.id === currentFeedback?.feedbackTypeId
			)?.length ||
			0 > 0

		// If the feedback is the same, we send a neutral feedback request
		if (isSameFeedback) {
			if (neutralFeedback && neutralFeedback.length > 0) {
				onSubmitFeedback(song.id, neutralFeedback[0].id)
			}
			return
		}

		/**
		 * We send the request normally if:
		 * - No previous feedback
		 * - The feedback is different from the previous feedback and it's a negative feedback
		 */
		if (!isPositive) {
			setSelectedSongForFeedback(song.id)
			setShowNegativeFeedbackOptionsModal(true)
			return
		}

		if (positiveFeedback && positiveFeedback.length > 0) {
			// Track positive feedback
			if (playlist) {
				trackFeedbackPositive(
					{
						playlistId: playlist.id,
						playlistTitle: playlist.title,
					},
					{
						songId: song.id,
						songTitle: song.metadata.title,
						artistName: song.metadata.artist,
					}
				)
			}
			onSubmitFeedback(song.id, positiveFeedback[0].id)
		}
	}

	// Reset playing state when song changes
	useEffect(() => {
		if (currentPlayingSong) {
			setIsPlayerPlaying(true) // Auto-play starts playing
		} else {
			setIsPlayerPlaying(false)
		}
	}, [currentPlayingSong])

	// Prevent overlap with Chatbase launcher when bottom player is visible.
	useEffect(() => {
		if (currentPlayingSong) {
			setChatbaseLauncherBottomOffset(140)
			return () => resetChatbaseLauncherBottomOffset()
		}
		resetChatbaseLauncherBottomOffset()
	}, [currentPlayingSong])

	// Handle favorite click - must be before early returns to maintain hook order
	const handleFavoriteClick = useCallback(() => {
		if (!playlist?.id || isFavoriting) return

		// Prevent duplicate mutations by checking if we've already initiated one recently
		const now = Date.now()
		const lastInitiated = lastMutationInitiatedRef.current
		if (
			lastInitiated &&
			lastInitiated.playlistId === playlist.id &&
			now - lastInitiated.timestamp < 500 // Within 500ms - very short window to prevent rapid double-clicks
		) {
			return // Skip duplicate mutation
		}

		const newFavoriteState = !isFavorite
		setIsFavoriting(true)

		// Track when mutation was initiated (for preventing duplicate mutations)
		lastMutationInitiatedRef.current = {
			playlistId: playlist.id,
			timestamp: now,
		}

		addToFavoritesMutation.mutate({
			playlistId: playlist.id,
			favorite: newFavoriteState,
		})
	}, [playlist?.id, isFavoriting, isFavorite, addToFavoritesMutation])

	// Handle next song (called by TrackPlayer onEnded for autoplay or onNext for manual)
	const handleNextSong = useCallback(
		(isAutoplay: boolean = false) => {
			if (!currentPlayingSong || filteredSongs.length <= 1) return

			const currentIndex = filteredSongs.findIndex(
				(s) => s.id === currentPlayingSong.id
			)
			const nextIndex =
				currentIndex < filteredSongs.length - 1 ? currentIndex + 1 : 0
			const nextSong = filteredSongs[nextIndex]

			if (playlist) {
				const playlistData = {
					playlistId: playlist.id,
					playlistTitle: playlist.title,
				}
				const currentSongData = {
					songId: currentPlayingSong.id,
					songTitle: currentPlayingSong.metadata.title,
					artistName: currentPlayingSong.metadata.artist,
				}
				const nextSongData = {
					songId: nextSong.id,
					songTitle: nextSong.metadata.title,
					artistName: nextSong.metadata.artist,
				}

				if (isAutoplay) {
					// Track autoplay event when song ends and next one starts automatically
					trackSongAutoplay(playlistData, currentSongData, nextSongData)
				} else {
					// Track manual next song click
					trackNextSong(playlistData, currentSongData, nextSongData)
				}
			}

			setCurrentPlayingSong(nextSong)
		},
		[
			currentPlayingSong,
			filteredSongs,
			playlist,
			trackSongAutoplay,
			trackNextSong,
		]
	)

	// Handle previous song
	const handlePreviousSong = useCallback(() => {
		if (!currentPlayingSong || filteredSongs.length <= 1) return

		const currentIndex = filteredSongs.findIndex(
			(s) => s.id === currentPlayingSong.id
		)
		const prevIndex =
			currentIndex > 0 ? currentIndex - 1 : filteredSongs.length - 1
		const prevSong = filteredSongs[prevIndex]

		// Track previous song click
		if (playlist) {
			trackPreviousSong(
				{
					playlistId: playlist.id,
					playlistTitle: playlist.title,
				},
				{
					songId: currentPlayingSong.id,
					songTitle: currentPlayingSong.metadata.title,
					artistName: currentPlayingSong.metadata.artist,
				},
				{
					songId: prevSong.id,
					songTitle: prevSong.metadata.title,
					artistName: prevSong.metadata.artist,
				}
			)
		}

		setCurrentPlayingSong(prevSong)
	}, [currentPlayingSong, filteredSongs, playlist, trackPreviousSong])

	// Handle play with tracking
	const handlePlay = useCallback(() => {
		setIsPlayerPlaying(true)
		if (playlist && currentPlayingSong) {
			trackSongResume(
				{
					playlistId: playlist.id,
					playlistTitle: playlist.title,
				},
				{
					songId: currentPlayingSong.id,
					songTitle: currentPlayingSong.metadata.title,
					artistName: currentPlayingSong.metadata.artist,
				}
			)
		}
	}, [playlist, currentPlayingSong, trackSongResume])

	// Handle pause with tracking
	const handlePause = useCallback(() => {
		setIsPlayerPlaying(false)
		if (playlist && currentPlayingSong) {
			trackSongPause(
				{
					playlistId: playlist.id,
					playlistTitle: playlist.title,
				},
				{
					songId: currentPlayingSong.id,
					songTitle: currentPlayingSong.metadata.title,
					artistName: currentPlayingSong.metadata.artist,
				}
			)
		}
	}, [playlist, currentPlayingSong, trackSongPause])

	// Handle close player with tracking
	const handleClosePlayer = useCallback(() => {
		if (playlist && currentPlayingSong) {
			trackPlayerClose(
				{
					playlistId: playlist.id,
					playlistTitle: playlist.title,
				},
				{
					songId: currentPlayingSong.id,
					songTitle: currentPlayingSong.metadata.title,
					artistName: currentPlayingSong.metadata.artist,
				}
			)
		}
		setCurrentPlayingSong(null)
	}, [playlist, currentPlayingSong, trackPlayerClose])

	if (isLoading) {
		return (
			<PageLayout
				{...(isDesktop
					? {
							title: pageTitle,
							backButton: { show: true, onClick: onBack },
						}
					: {})}
			>
				<div className="mb-6 lg:mb-8">
					<div className="flex gap-4 lg:gap-8 items-stretch flex-col md:flex-row md:items-start">
						<Skeleton className="w-[120px] h-[120px] md:w-[200px] md:h-[200px] rounded-lg" />
						<div className="flex-1 flex flex-col gap-3 lg:gap-4 justify-between">
							<Skeleton className="h-6 w-48 lg:h-8 lg:w-64 mb-2 lg:mb-4" />
							<Skeleton className="h-16 lg:h-20 w-full mb-2 lg:mb-4" />
							<div className="flex flex-wrap gap-2 lg:gap-4">
								<Skeleton className="h-6 w-20 lg:h-8 lg:w-24" />
								<Skeleton className="h-6 w-24 lg:h-8 lg:w-32" />
								<Skeleton className="h-6 w-28 lg:h-8 lg:w-40" />
							</div>
						</div>
					</div>
				</div>
				<div className="flex justify-between gap-4 mb-6 lg:mb-8 items-center flex-col lg:flex-row lg:items-stretch">
					<Skeleton className="h-10 w-full lg:max-w-[400px]" />
					<div className="flex gap-2 w-full lg:w-auto">
						<Skeleton className="h-10 flex-1 lg:w-32" />
						<Skeleton className="h-10 flex-1 lg:w-40" />
					</div>
				</div>
				<div className="overflow-hidden">
					<Skeleton className="h-10 lg:h-12 w-full mb-2" />
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-10 lg:h-12 w-full mb-2" />
					))}
				</div>
			</PageLayout>
		)
	}

	if (!playlist) {
		return (
			<PageLayout
				{...(isDesktop
					? {
							title: pageTitle,
							backButton: { show: true, onClick: onBack },
						}
					: {})}
			>
				<div className="flex justify-center items-center min-h-[400px]">
					<p>{t('entities.music.playlist_not_found')}</p>
				</div>
			</PageLayout>
		)
	}

	const updatedDate = currentLanguage?.startsWith('es')
		? playlist.updatedAt.toFormat('MM-dd-yyyy')
		: playlist.updatedAt.toFormat('yyyy-MM-dd')

	// Show review buttons only when reviewed is explicitly false
	// If reviewed is true, null, undefined, or doesn't exist, show regular buttons
	const needsReview = playlist.reviewed === false

	const renderRequestModificationsButton = () => {
		// Hide entirely when not actionable (public playlist, plan doesn't allow it, or no handler)
		if (
			playlist.public ||
			!canRequestModifications ||
			!onRequestModificationsClick
		) {
			return null
		}
		return (
			<Button
				type="button"
				variant="outline"
				onClick={onRequestModificationsClick}
				className="bg-transparent border border-foreground/10 dark:border-white/10 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
			>
				{t('entities.music.request_modifications')}
				<Icon name="pencil" width="16px" height="16px" color="currentColor" />
			</Button>
		)
	}

	return (
		<>
			<PageLayout
				{...(isDesktop
					? {
							title: pageTitle,
							backButton: { show: true, onClick: onBack },
						}
					: {})}
				// MobileDrawerMenu is fixed at the bottom on mobile (h-[95px]).
				// Add bottom padding so the last songs can scroll above it.
				// If the TrackPlayer is also visible, reserve space for both.
				contentClassName={
					currentPlayingSong
						? 'pb-[100px] md:pb-[80px] lg:pb-[80px]'
						: 'pb-[95px] md:pb-0'
				}
				topAppBarLeftContent={
					<div className="md:hidden">
						<button
							onClick={onBack}
							className="w-9 h-9 rounded-full bg-background/30 text-foreground flex items-center justify-center font-semibold text-base cursor-pointer transition-colors hover:bg-background/20"
							aria-label={t('actions.back', 'Back')}
						>
							<Icon name="arrow-left" width="20px" height="20px" />
						</button>
					</div>
				}
			>
				<div className="w-full">
					{/* Header Section */}
					<PlaylistHeader
						playlist={playlist}
						filteredSongs={filteredSongs}
						updatedDate={updatedDate}
						isFavorite={isFavorite}
						isFavoriting={isFavoriting}
						handleFavoriteClick={handleFavoriteClick}
						onPlayNow={() => {
							if (filteredSongs.length > 0) {
								setCurrentPlayingSong(filteredSongs[0])
							}
						}}
						canRequestModifications={canRequestModifications}
						onRequestModificationsClick={onRequestModificationsClick}
						onRateClick={onRateClick}
						canAssignPlaylists={canAssignPlaylists}
						onManageAssignmentClick={() => onAssignPlaylistModalChange(true)}
					/>

					{/* Search and Actions Section */}
					<div className="flex lg:justify-between gap-4 mb-8 lg:items-center max-md:flex-col w-full max-w-full">
						<div className="relative flex-1 max-w-[400px] flex items-center w-full">
							<Icon
								name="search"
								width="16px"
								height="16px"
								color="var(--color-border)"
								className="absolute left-4 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none z-[1]"
							/>
							<Input
								type="search"
								placeholder={t('actions.search')}
								value={searchQuery}
								onChange={(e) => onSearchChange(e.target.value)}
								className="pl-[calc(16px+16px+8px)] w-full bg-[var(--shade)] border border-foreground/10 rounded-3xl text-foreground text-base placeholder:text-muted-foreground placeholder:opacity-80 focus:outline-none focus:border-primary"
							/>
						</div>

						{needsReview ? (
							<div className="flex gap-2 items-center">
								{canAssignPlaylists && (
									<Button
										variant="outline"
										className="rounded-full cursor-pointer"
										onClick={() => onAssignPlaylistModalChange(true)}
									>
										{t('entities.playlist.manage_assignment')}
										<MapPin className="!h-4 !w-4" />
									</Button>
								)}
								{renderRequestModificationsButton()}
								<Button
									variant="default"
									className="rounded-full"
									onClick={onApprovePlaylist}
									disabled={isApproving || !onApprovePlaylist}
								>
									{t('entities.music.approve_playlist')}
									<Icon name="check-sm" width="16px" height="16px" />
								</Button>
							</div>
						) : (
							// only desktop
							<div className="hidden md:flex gap-2 lg:items-center flex-wrap lg:justify-end">
								<div className="flex items-center">
									<Select
										value={statusFilter}
										onValueChange={(value: 'all' | 'disliked') =>
											onStatusFilterChange(value)
										}
									>
										<SelectTrigger className="h-10 w-full lg:min-w-[180px] rounded-full bg-transparent dark:bg-white/10 border border-foreground/10 dark:border-white/10 text-foreground focus:border-primary">
											<SelectValue>
												{statusFilter === 'all'
													? t('entities.music.status_all_songs')
													: t('entities.music.status_only_disliked_songs')}
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											<SelectItem
												value="all"
												className="relative [&>span:first-of-type]:!hidden"
											>
												{t('entities.music.status_all_songs')}
												<span className="absolute right-2 top-1/2 -translate-y-1/2 flex size-4 items-center justify-center rounded-full border border-white min-w-[16px] min-h-[16px]">
													<SelectPrimitive.ItemIndicator className="flex items-center justify-center">
														<CheckIcon className="size-3 text-white" />
													</SelectPrimitive.ItemIndicator>
												</span>
											</SelectItem>
											<SelectItem
												value="disliked"
												className="relative [&>span:first-of-type]:!hidden"
											>
												{t('entities.music.status_only_disliked_songs')}
												<span className="absolute right-2 top-1/2 -translate-y-1/2 flex size-4 items-center justify-center rounded-full border border-white min-w-[16px] min-h-[16px]">
													<SelectPrimitive.ItemIndicator className="flex items-center justify-center">
														<CheckIcon className="size-3 text-white" />
													</SelectPrimitive.ItemIndicator>
												</span>
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
								{canAssignPlaylists && (
									<Button
										variant="outline"
										className="rounded-full cursor-pointer"
										onClick={() => onAssignPlaylistModalChange(true)}
									>
										{t('entities.playlist.manage_assignment')}
										<MapPin className="!h-4 !w-4" />
									</Button>
								)}
								{renderRequestModificationsButton()}
								<Button
									variant="outline"
									className="rounded-full"
									onClick={onRateClick}
								>
									{t('entities.music.rate')}
									<Icon
										name="star"
										width="16px"
										height="16px"
										color="var(--foreground)"
									/>
								</Button>
							</div>
						)}
					</div>

					{/* Songs Table */}
					<div className={cn('w-full', currentPlayingSong && 'mb-[60px]')}>
						{feedbackTypes && feedbackTypes.length > 0 && (
							<PlaylistSongs
								playlist={playlist}
								filteredSongs={filteredSongs}
								songFeedbacks={songFeedbacks}
								feedbackTypes={feedbackTypes || []}
								isLoadingFeedback={isLoadingFeedback}
								currentPlayingSong={currentPlayingSong}
								hoveredFeedbackSongId={hoveredFeedbackSongId}
								playlistFeedbacks={playlistFeedbacks || []}
								allFeedbackTypes={allFeedbackTypes}
								isPlayerPlaying={isPlayerPlaying}
								setIsPlayerPlaying={setIsPlayerPlaying}
								handleFeedbackClick={handleFeedbackClick}
								setHoveredFeedbackSongId={setHoveredFeedbackSongId}
								setCurrentPlayingSong={setCurrentPlayingSong}
								searchQuery={searchQuery}
								selectedSongForFeedback={selectedSongForFeedback}
								isSubmittingFeedback={isSubmittingFeedback}
							/>
						)}
					</div>

					{/* Feedback Drop Ratio Modal */}
					<Dialog
						open={showFeedbackDropModal}
						onOpenChange={onCloseFeedbackDropModal}
					>
						<DialogContent className="max-w-md">
							<DialogHeader>
								<DialogTitle className="text-xl font-bold">
									{t('entities.music.feedback_drop_title')}
								</DialogTitle>
							</DialogHeader>
							<div className="space-y-4">
								<p className="text-sm text-muted-foreground">
									{t('entities.music.feedback_drop_message')}
								</p>
								<div className="flex gap-3 pt-4">
									<button
										onClick={onCloseFeedbackDropModal}
										className="flex-1 px-4 py-2 rounded-full text-base font-medium cursor-pointer transition-opacity duration-200 bg-neutral text-white border border-border/10 hover:opacity-80"
									>
										{t('actions.cancel')}
									</button>
									<button
										onClick={() => {
											onCloseFeedbackDropModal()
											if (ratePlaylistUrl) {
												window.open(ratePlaylistUrl, '_blank')
											}
										}}
										className="flex-1 px-4 py-2 rounded-full text-base font-medium cursor-pointer transition-opacity duration-200 bg-white text-black border-none hover:opacity-80"
									>
										{t('entities.music.complete_form')}
									</button>
								</div>
							</div>
						</DialogContent>
					</Dialog>

					{/* Rate Playlist Modal */}
					<Dialog
						open={showRateModal && !!ratePlaylistUrl}
						onOpenChange={onCloseRateModal}
					>
						<DialogContent className="max-w-4xl w-full h-[90vh] p-0">
							<DialogHeader className="p-6 pb-0">
								<DialogTitle>{t('entities.music.rate')}</DialogTitle>
							</DialogHeader>
							{ratePlaylistUrl && (
								<div className="flex-1 min-h-[80vh] p-6 pt-0">
									<iframe
										src={ratePlaylistUrl}
										width="100%"
										height="100%"
										className="border-0 rounded-lg"
										title="Rate Playlist Form"
									/>
								</div>
							)}
						</DialogContent>
					</Dialog>

					{/* Dislike Feedback Modal */}
					<Dialog
						open={showDislikeFeedbackModal && !!dislikeFeedbackUrl}
						onOpenChange={onCloseDislikeFeedbackModal}
					>
						<DialogContent className="max-w-4xl w-full h-[90vh] p-0">
							<DialogHeader className="p-6 pb-0">
								<DialogTitle>{t('entities.music.feedback')}</DialogTitle>
							</DialogHeader>
							{dislikeFeedbackUrl && (
								<div className="flex-1 min-h-[80vh] p-6 pt-0">
									<iframe
										src={dislikeFeedbackUrl}
										width="100%"
										height="100%"
										className="border-0 rounded-lg"
										title="Feedback Form"
									/>
								</div>
							)}
						</DialogContent>
					</Dialog>

					{/* Request Modifications Modal */}
					<Dialog
						open={showRequestModificationsModal && !!requestModificationsUrl}
						onOpenChange={onCloseRequestModificationsModal}
					>
						<DialogContent className="max-w-4xl w-full h-[90vh] p-0">
							<DialogHeader className="p-6 pb-0">
								<DialogTitle>
									{t('entities.music.request_modifications')}
								</DialogTitle>
							</DialogHeader>
							{requestModificationsUrl && (
								<div className="flex-1 min-h-[80vh] p-6 pt-0">
									<iframe
										src={requestModificationsUrl}
										width="100%"
										height="100%"
										className="border-0 rounded-lg"
										title="Request Modifications Form"
									/>
								</div>
							)}
						</DialogContent>
					</Dialog>

					{/* Negative Feedback Options Modal */}
					{feedbackTypes && (
						<ChooseNegativeFeedbackModal
							showNegativeFeedbackOptionsModal={
								showNegativeFeedbackOptionsModal
							}
							onClose={onCloseNegativeFeedbackOptionsModal}
							onConfirm={(selectedNegativeFeedbackTypeId: number) => {
								// Track negative feedback
								if (playlist && selectedSongForFeedback) {
									const song = filteredSongs.find(
										(s) => s.id === selectedSongForFeedback
									)
									const feedbackType = feedbackTypes.find(
										(ft) => ft.id === selectedNegativeFeedbackTypeId
									)
									if (song) {
										trackFeedbackNegative(
											{
												playlistId: playlist.id,
												playlistTitle: playlist.title,
											},
											{
												songId: song.id,
												songTitle: song.metadata.title,
												artistName: song.metadata.artist,
											},
											feedbackType?.name
										)
									}
								}
								onSubmitFeedback(
									selectedSongForFeedback || 0,
									selectedNegativeFeedbackTypeId
								)
								onCloseNegativeFeedbackOptionsModal()
							}}
							feedbackTypes={feedbackTypes}
						/>
					)}
				</div>
			</PageLayout>
			{/* Track Player - Floating at bottom - Outside PageLayout for full width */}
			{currentPlayingSong && (
				<TrackPlayer
					currentPlayingSong={currentPlayingSong}
					playlist={playlist}
					filteredSongs={filteredSongs}
					handlePreviousSong={handlePreviousSong}
					handleNextSong={handleNextSong}
					onPlay={handlePlay}
					onPause={handlePause}
					audioElementRef={audioElementRef}
					isMuted={isMuted}
					setIsMuted={setIsMuted}
					volume={volume}
					setVolume={setVolume}
					showFeedbackIcons={showFeedbackIcons}
					submittingFeedback={new Map<number, string>()}
					songFeedbacks={songFeedbacks}
					isLoadingFeedback={isLoadingFeedback}
					handleFeedbackClick={handleFeedbackClick}
					showCloseButton={showCloseButton}
					onClosePlayer={handleClosePlayer}
				/>
			)}

			{/* Assign Playlist Modal */}
			<AssignPlaylistModal
				open={showAssignPlaylistModal}
				onOpenChange={onAssignPlaylistModalChange}
				playlistId={playlist?.id}
				playlistName={playlist?.title}
			/>
		</>
	)
}

export default PlaylistDetailsPageUI
